import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTeams } from '../hooks/useTeams';
import { api } from '../hooks/useApi';
import { Card, Button, Input, Spinner, Badge, EmptyState, PageHeader } from '../components/ui';
import type { Employee, LoginLog, Team } from '@ns-b2b/shared';

type AdminTab = 'users' | 'teams' | 'logs' | 'exports' | 'system';

export function Admin() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<AdminTab>('users');
    const isAdmin = user?.effectiveRole === 'ADMIN';
    const isExec = user?.effectiveRole === 'EXECUTIVE';

    const tabs: { id: AdminTab; label: string; adminOnly?: boolean; execOnly?: boolean }[] = [
        { id: 'users', label: 'ผู้ใช้' },
        { id: 'teams', label: 'ทีม', execOnly: true },
        { id: 'logs', label: 'Login Logs', adminOnly: true },
        { id: 'exports', label: 'Export' },
        { id: 'system', label: 'ระบบ', adminOnly: true },
    ];

    const visibleTabs = tabs.filter((t) => {
        if (t.adminOnly && !isAdmin) return false;
        if (t.execOnly && !isAdmin && !isExec) return false;
        return true;
    });

    return (
        <div className="space-y-6">
            <PageHeader title="จัดการระบบ" subtitle="บริหารจัดการผู้ใช้และการตั้งค่า" />

            {/* Sub-tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2">
                {visibleTabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-5 py-2.5 rounded-xl font-medium text-sm whitespace-nowrap transition-all
              ${activeTab === tab.id
                                ? 'bg-primary text-white shadow-soft'
                                : 'bg-white text-neutral-600 border border-neutral-200 hover:border-neutral-300'
                            }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'users' && <UsersTab />}
            {activeTab === 'teams' && (isAdmin || isExec) && <TeamsTab />}
            {activeTab === 'logs' && isAdmin && <LogsTab />}
            {activeTab === 'exports' && <ExportsTab />}
            {activeTab === 'system' && isAdmin && <SystemTab />}
        </div>
    );
}

function UsersTab() {
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => { loadEmployees(); }, []);

    const loadEmployees = async () => {
        setIsLoading(true);
        const { data } = await api<{ data: Employee[] }>('/api/employees');
        if (data) setEmployees((data as unknown as { data: Employee[] }).data || []);
        setIsLoading(false);
    };

    const handleApprove = async (id: string) => {
        await api(`/api/employees/${id}`, { method: 'PUT', body: JSON.stringify({ status: 'active' }) });
        loadEmployees();
    };

    if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

    return (
        <Card padding="none" className="overflow-hidden">
            <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
                <h2 className="font-semibold text-neutral-900">รายชื่อผู้ใช้</h2>
                <Button onClick={loadEmployees} variant="secondary" size="sm">รีเฟรช</Button>
            </div>
            {employees.length === 0 ? (
                <div className="p-8"><EmptyState icon="👥" title="ไม่มีผู้ใช้" /></div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-neutral-50">
                                <th className="text-left p-4 font-semibold text-neutral-700 text-sm">ชื่อ</th>
                                <th className="text-left p-4 font-semibold text-neutral-700 text-sm">ทีม</th>
                                <th className="text-left p-4 font-semibold text-neutral-700 text-sm">บทบาท</th>
                                <th className="text-left p-4 font-semibold text-neutral-700 text-sm">สถานะ</th>
                                <th className="text-left p-4 font-semibold text-neutral-700 text-sm">การดำเนินการ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employees.map((emp) => (
                                <tr key={emp.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                                    <td className="p-4 font-medium text-neutral-900">{emp.name}</td>
                                    <td className="p-4 text-sm">ทีม {emp.team}</td>
                                    <td className="p-4"><Badge variant="neutral">{emp.role || 'EMPLOYEE'}</Badge></td>
                                    <td className="p-4">
                                        <Badge variant={emp.status === 'active' ? 'success' : 'warning'}>
                                            {emp.status}
                                        </Badge>
                                    </td>
                                    <td className="p-4">
                                        {emp.status === 'pending' && (
                                            <Button onClick={() => handleApprove(emp.id)} variant="primary" size="sm">
                                                อนุมัติ
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
}

function TeamsTab() {
    const { refreshTeams } = useTeams();
    const [allTeams, setAllTeams] = useState<Team[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editingTeam, setEditingTeam] = useState<Team | null>(null);
    const [newTeam, setNewTeam] = useState({ name: '', code: '', color: '#F97316' });
    const [showAddForm, setShowAddForm] = useState(false);

    useEffect(() => { loadAllTeams(); }, []);

    const loadAllTeams = async () => {
        setIsLoading(true);
        const { data } = await api<{ data: Team[] }>('/api/teams/all');
        if (data) setAllTeams((data as unknown as { data: Team[] }).data || []);
        setIsLoading(false);
    };

    const handleAddTeam = async () => {
        if (!newTeam.name || !newTeam.code) {
            alert('กรุณากรอกชื่อและรหัสทีม');
            return;
        }
        await api('/api/teams', {
            method: 'POST',
            body: JSON.stringify({
                name: newTeam.name,
                code: newTeam.code.toUpperCase(),
                color: newTeam.color,
            }),
        });
        setNewTeam({ name: '', code: '', color: '#F97316' });
        setShowAddForm(false);
        loadAllTeams();
        refreshTeams();
    };

    const handleUpdateTeam = async () => {
        if (!editingTeam) return;
        await api(`/api/teams/${editingTeam.id}`, {
            method: 'PUT',
            body: JSON.stringify({
                name: editingTeam.name,
                code: editingTeam.code,
                color: editingTeam.color,
                is_active: editingTeam.is_active,
            }),
        });
        setEditingTeam(null);
        loadAllTeams();
        refreshTeams();
    };

    const handleToggleActive = async (team: Team) => {
        await api(`/api/teams/${team.id}`, {
            method: 'PUT',
            body: JSON.stringify({ is_active: team.is_active ? 0 : 1 }),
        });
        loadAllTeams();
        refreshTeams();
    };

    if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

    return (
        <div className="space-y-6">
            {/* Add Team Form */}
            <Card padding="lg">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="font-semibold text-neutral-900">จัดการทีม</h2>
                    <Button onClick={() => setShowAddForm(!showAddForm)} variant="primary" size="sm">
                        {showAddForm ? 'ยกเลิก' : '+ เพิ่มทีมใหม่'}
                    </Button>
                </div>

                {showAddForm && (
                    <div className="bg-neutral-50 rounded-xl p-4 mb-4 animate-in">
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                            <Input
                                label="ชื่อทีม"
                                value={newTeam.name}
                                onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                                placeholder="เช่น ทีม A"
                            />
                            <Input
                                label="รหัสทีม"
                                value={newTeam.code}
                                onChange={(e) => setNewTeam({ ...newTeam, code: e.target.value.toUpperCase() })}
                                placeholder="เช่น A"
                                maxLength={5}
                            />
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-2">สี</label>
                                <input
                                    type="color"
                                    value={newTeam.color}
                                    onChange={(e) => setNewTeam({ ...newTeam, color: e.target.value })}
                                    className="w-full h-10 rounded-lg cursor-pointer"
                                />
                            </div>
                            <div className="flex items-end">
                                <Button onClick={handleAddTeam} variant="primary" className="w-full">
                                    บันทึก
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Card>

            {/* Teams List */}
            <Card padding="none" className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-neutral-50">
                                <th className="text-left p-4 font-semibold text-neutral-700 text-sm">สี</th>
                                <th className="text-left p-4 font-semibold text-neutral-700 text-sm">รหัส</th>
                                <th className="text-left p-4 font-semibold text-neutral-700 text-sm">ชื่อทีม</th>
                                <th className="text-left p-4 font-semibold text-neutral-700 text-sm">สถานะ</th>
                                <th className="text-left p-4 font-semibold text-neutral-700 text-sm">การดำเนินการ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allTeams.map((team) => (
                                <tr key={team.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                                    <td className="p-4">
                                        <div
                                            className="w-8 h-8 rounded-lg"
                                            style={{ backgroundColor: team.color }}
                                        />
                                    </td>
                                    <td className="p-4 font-mono font-semibold text-neutral-900">{team.code}</td>
                                    <td className="p-4">
                                        {editingTeam?.id === team.id ? (
                                            <Input
                                                value={editingTeam.name}
                                                onChange={(e) => setEditingTeam({ ...editingTeam, name: e.target.value })}
                                            />
                                        ) : (
                                            <span className="font-medium text-neutral-900">{team.name}</span>
                                        )}
                                    </td>
                                    <td className="p-4">
                                        <Badge variant={team.is_active ? 'success' : 'neutral'}>
                                            {team.is_active ? 'ใช้งาน' : 'ปิดใช้งาน'}
                                        </Badge>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex gap-2">
                                            {editingTeam?.id === team.id ? (
                                                <>
                                                    <Button onClick={handleUpdateTeam} variant="primary" size="sm">บันทึก</Button>
                                                    <Button onClick={() => setEditingTeam(null)} variant="secondary" size="sm">ยกเลิก</Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button onClick={() => setEditingTeam(team)} variant="secondary" size="sm">แก้ไข</Button>
                                                    <Button
                                                        onClick={() => handleToggleActive(team)}
                                                        variant={team.is_active ? 'ghost' : 'primary'}
                                                        size="sm"
                                                    >
                                                        {team.is_active ? 'ปิด' : 'เปิด'}
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}

function LogsTab() {
    const [logs, setLogs] = useState<LoginLog[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => { loadLogs(); }, []);

    const loadLogs = async () => {
        setIsLoading(true);
        const { data } = await api<{ data: LoginLog[] }>('/api/login-logs?limit=100');
        if (data) setLogs((data as unknown as { data: LoginLog[] }).data || []);
        setIsLoading(false);
    };

    if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

    return (
        <Card padding="none" className="overflow-hidden">
            <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
                <h2 className="font-semibold text-neutral-900">Login Logs</h2>
                <Button onClick={loadLogs} variant="secondary" size="sm">รีเฟรช</Button>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-neutral-50">
                            <th className="text-left p-3 font-semibold text-neutral-700">เวลา</th>
                            <th className="text-left p-3 font-semibold text-neutral-700">ผู้ใช้</th>
                            <th className="text-left p-3 font-semibold text-neutral-700">ผลลัพธ์</th>
                            <th className="text-left p-3 font-semibold text-neutral-700">IP</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map((log) => (
                            <tr key={log.id} className="border-t border-neutral-100">
                                <td className="p-3 font-mono text-xs text-neutral-500">{log.ts}</td>
                                <td className="p-3">{log.user_name || '-'}</td>
                                <td className="p-3">
                                    <Badge variant={log.result.startsWith('ALLOW') ? 'success' : 'error'}>
                                        {log.result}
                                    </Badge>
                                </td>
                                <td className="p-3 font-mono text-xs text-neutral-500">{log.ip}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

function ExportsTab() {
    const [dateRange, setDateRange] = useState({
        from: new Date(new Date().setDate(1)).toISOString().split('T')[0],
        to: new Date().toISOString().split('T')[0],
    });

    return (
        <Card padding="lg">
            <h2 className="font-semibold text-neutral-900 mb-6">Export ข้อมูล</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
                <Input label="จากวันที่" type="date" value={dateRange.from}
                    onChange={(e) => setDateRange((p) => ({ ...p, from: e.target.value }))} />
                <Input label="ถึงวันที่" type="date" value={dateRange.to}
                    onChange={(e) => setDateRange((p) => ({ ...p, to: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <Button variant="primary" onClick={() => window.open(`/api/export/visits.csv?from=${dateRange.from}&to=${dateRange.to}`, '_blank')}>
                    Export หน้างาน
                </Button>
                <Button variant="secondary" onClick={() => window.open(`/api/export/login-logs.csv?from=${dateRange.from}&to=${dateRange.to}`, '_blank')}>
                    Export Login Logs
                </Button>
            </div>
        </Card>
    );
}

function SystemTab() {
    const [retentionDays, setRetentionDays] = useState(45);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        (async () => {
            const { data } = await api<{ retentionDays: number }>('/api/admin/settings/photos');
            if (data) {
                const settings = (data as unknown as { data: { retentionDays: number } }).data;
                if (settings) setRetentionDays(settings.retentionDays);
            }
            setIsLoading(false);
        })();
    }, []);

    const handleSave = async () => {
        await api('/api/admin/settings/photos', { method: 'POST', body: JSON.stringify({ retentionDays }) });
        alert('บันทึกสำเร็จ!');
    };

    const handleCleanup = async () => {
        if (!confirm('ต้องการลบรูปภาพเก่าหรือไม่?')) return;
        await api('/api/admin/photos/cleanup', { method: 'POST' });
        alert('ลบรูปภาพเก่าสำเร็จ!');
    };

    if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

    return (
        <Card padding="lg">
            <h2 className="font-semibold text-neutral-900 mb-6">การตั้งค่าระบบ</h2>
            <div className="space-y-6">
                <div>
                    <h3 className="font-medium text-neutral-700 mb-3">การเก็บรักษารูปภาพ</h3>
                    <div className="flex gap-4 items-end">
                        <Input label="จำนวนวันที่เก็บ" type="number" value={retentionDays}
                            onChange={(e) => setRetentionDays(parseInt(e.target.value) || 45)} min={1} max={365} />
                        <Button onClick={handleSave} variant="primary">บันทึก</Button>
                    </div>
                </div>
                <div>
                    <h3 className="font-medium text-neutral-700 mb-3">ลบรูปภาพเก่า</h3>
                    <Button onClick={handleCleanup} variant="secondary">รัน Cleanup</Button>
                </div>
            </div>
        </Card>
    );
}
